import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, X, Moon, Sparkles, TreePine } from 'lucide-react';
import api from '../../lib/api';

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const MOON_ICONS = {
  nueva: '🌑', creciente: '🌒', cuarto_creciente: '🌓',
  gibosa_creciente: '🌔', llena: '🌕', gibosa_menguante: '🌖',
  cuarto_menguante: '🌗', menguante: '🌘',
};

const ELEMENT_COLORS = {
  Agua: { bg: 'rgba(100,140,200,0.10)', border: 'rgba(100,140,200,0.25)', dot: '#6e9ecf' },
  Fuego: { bg: 'rgba(200,120,60,0.10)', border: 'rgba(200,120,60,0.25)', dot: '#c87838' },
  Aire: { bg: 'rgba(170,170,200,0.10)', border: 'rgba(170,170,200,0.25)', dot: '#aaa8cc' },
  Éter: { bg: 'rgba(160,120,200,0.10)', border: 'rgba(160,120,200,0.25)', dot: '#a078c8' },
  Tierra: { bg: 'rgba(140,160,100,0.10)', border: 'rgba(140,160,100,0.25)', dot: '#8ca064' },
  Luz: { bg: 'rgba(201,162,39,0.10)', border: 'rgba(201,162,39,0.25)', dot: '#c9a227' },
};

const CalendarioEnergetico = ({ onBack }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => { fetchMonth(); }, [year, month]);

  const fetchMonth = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/campus/calendario/${year}/${month}`);
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  };

  const goPrev = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
    setSelectedDay(null);
  };
  const goNext = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
    setSelectedDay(null);
  };

  const today = now.getDate();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

  // Build grid cells (padding before first day)
  const gridCells = [];
  if (data) {
    const pad = data.first_weekday; // 0=Mon
    for (let i = 0; i < pad; i++) gridCells.push(null);
    data.days.forEach(d => gridCells.push(d));
  }

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid="calendario-energetico">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={onBack}
          className="flex items-center gap-2 text-[#5c4a2a]/30 hover:text-[#5c4a2a]/60 transition-colors text-sm mb-8"
          data-testid="calendario-back-btn">
          <ArrowLeft className="w-4 h-4" /> Volver al jardín
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-6 mb-2">
            <button onClick={goPrev} className="text-[#c9a227]/40 hover:text-[#c9a227]/80 transition-colors p-1" data-testid="cal-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="text-[#4a3818]/70 text-xl font-light tracking-wide min-w-[200px]"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              {MONTH_NAMES[month - 1]} {year}
            </h1>
            <button onClick={goNext} className="text-[#c9a227]/40 hover:text-[#c9a227]/80 transition-colors p-1" data-testid="cal-next">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <p className="text-[#8b7340]/30 text-xs tracking-wider">Calendario Energético</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !data ? (
          <p className="text-center text-[#8b7340]/30 py-12">No se pudo cargar el calendario</p>
        ) : (
          <>
            {/* Day names header */}
            <div className="grid grid-cols-7 mb-1">
              {DAY_NAMES.map(d => (
                <div key={d} className="text-center text-[10px] tracking-wider uppercase text-[#8b7340]/35 py-2 font-medium">{d}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
              {gridCells.map((cell, i) => {
                if (!cell) return <div key={`pad-${i}`} className="aspect-square" />;
                const d = cell;
                const isToday = isCurrentMonth && d.day === today;
                const hasPortal = d.markers.some(m => m.type === 'portal');
                const hasSabbat = d.markers.some(m => m.type === 'sabbat');
                const hasCustom = d.markers.some(m => m.type === 'custom');
                const isMajorMoon = d.moon_phase === 'nueva' || d.moon_phase === 'llena';
                const elColor = ELEMENT_COLORS[d.element] || ELEMENT_COLORS.Luz;

                return (
                  <button key={d.day}
                    onClick={() => setSelectedDay(selectedDay?.day === d.day ? null : d)}
                    className={`relative aspect-square rounded-lg p-1 transition-all text-center flex flex-col items-center justify-start gap-0.5 group
                      ${isToday ? 'ring-1 ring-[#c9a227]/40' : ''}
                      ${selectedDay?.day === d.day ? 'ring-2 ring-[#c9a227]/60 shadow-md' : ''}
                    `}
                    style={{
                      background: isToday ? 'rgba(201,162,39,0.08)' : elColor.bg,
                      border: `1px solid ${isToday ? 'rgba(201,162,39,0.3)' : elColor.border}`,
                    }}
                    data-testid={`cal-day-${d.day}`}
                  >
                    {/* Day number */}
                    <span className={`text-sm font-medium ${isToday ? 'text-[#c9a227]' : 'text-[#4a3818]/60'}`}>
                      {d.day}
                    </span>

                    {/* Moon icon */}
                    <span className="text-[10px] leading-none opacity-60">{MOON_ICONS[d.moon_phase]}</span>

                    {/* Markers row */}
                    <div className="flex gap-0.5 items-center justify-center flex-wrap">
                      {hasPortal && <span className="w-1.5 h-1.5 rounded-full bg-purple-400/70" title="Portal" />}
                      {hasSabbat && <span className="w-1.5 h-1.5 rounded-full bg-green-500/70" title="Sabbat" />}
                      {hasCustom && <span className="w-1.5 h-1.5 rounded-full bg-amber-500/70" title="Evento" />}
                      {isMajorMoon && <span className="w-1.5 h-1.5 rounded-full bg-blue-300/70" title="Luna" />}
                    </div>

                    {/* Element dot */}
                    <div className="absolute bottom-0.5 right-1">
                      <span className="w-1 h-1 rounded-full block" style={{ background: elColor.dot, opacity: 0.4 }} />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
              {[
                { color: 'bg-purple-400/70', label: 'Portal' },
                { color: 'bg-green-500/70', label: 'Sabbat' },
                { color: 'bg-blue-300/70', label: 'Luna mayor' },
                { color: 'bg-amber-500/70', label: 'Evento' },
              ].map(l => (
                <div key={l.label} className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${l.color}`} />
                  <span className="text-[10px] text-[#8b7340]/35">{l.label}</span>
                </div>
              ))}
            </div>

            {/* Selected day detail */}
            {selectedDay && <DayDetail day={selectedDay} onClose={() => setSelectedDay(null)} />}
          </>
        )}
      </div>
    </div>
  );
};

/* ═══ Day Detail Panel ═══ */
const DayDetail = ({ day, onClose }) => {
  const el = ELEMENT_COLORS[day.element] || ELEMENT_COLORS.Luz;
  return (
    <div className="mt-6 rounded-xl border border-[#c9a227]/15 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300"
      style={{ background: 'linear-gradient(180deg, #faf8f4, #f5f0e6)' }}
      data-testid="cal-day-detail">

      <div className="flex items-center justify-between px-5 py-3 border-b border-[#c9a227]/10"
        style={{ background: el.bg }}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">{MOON_ICONS[day.moon_phase]}</span>
          <div>
            <span className="text-[#4a3818]/70 text-lg font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              Día {day.day}
            </span>
            <span className="text-[#8b7340]/40 text-xs ml-2">{DAY_NAMES[day.weekday]}</span>
          </div>
        </div>
        <button onClick={onClose} className="text-[#8b7340]/30 hover:text-[#8b7340]/60 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-5 py-4 space-y-3">
        {/* Element */}
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: el.dot }} />
          <span className="text-[#4a3818]/60 text-sm">{day.element}</span>
        </div>

        {/* Moon message */}
        <div className="flex items-start gap-2">
          <Moon className="w-3.5 h-3.5 text-blue-300/60 mt-0.5 flex-shrink-0" />
          <p className="text-[#4a3818]/50 text-sm">{day.moon_msg}</p>
        </div>

        {/* Markers */}
        {day.markers.map((m, i) => (
          <div key={i} className="flex items-start gap-2">
            {m.type === 'portal' && <Sparkles className="w-3.5 h-3.5 text-purple-400/60 mt-0.5 flex-shrink-0" />}
            {m.type === 'sabbat' && <TreePine className="w-3.5 h-3.5 text-green-500/60 mt-0.5 flex-shrink-0" />}
            {m.type === 'luna' && <Moon className="w-3.5 h-3.5 text-blue-300/60 mt-0.5 flex-shrink-0" />}
            {m.type === 'custom' && <Sparkles className="w-3.5 h-3.5 text-amber-500/60 mt-0.5 flex-shrink-0" />}
            <div>
              <p className="text-[#4a3818]/60 text-sm">{m.label}</p>
              {m.description && <p className="text-[#8b7340]/35 text-xs mt-0.5">{m.description}</p>}
            </div>
          </div>
        ))}

        {day.markers.length === 0 && (
          <p className="text-[#8b7340]/30 text-xs italic">Un día para estar presente y confiar en tu proceso.</p>
        )}
      </div>
    </div>
  );
};

export default CalendarioEnergetico;
